import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import {
  getPageFromStorage,
  getPageFromURL,
  getQueryFromStorage,
  getQueryFromURL,
  getSortFromStorage,
  getSortFromURL,
} from '~/components/dynamic_table/helpers/browserState'

const DynamicTableContext = createContext()

export function useDynamicTable() {
  return useContext(DynamicTableContext)
}

function noop() {}

// DynamicTable Props interface (+ default values)
const DEFAULT_PROPS = {
  id: null,
  url: null,
  method: 'get',
  data: [],
  columns: [],
  search: {
    fields: [],
    query: {},
    meta: {
      total: 0,
      current_page: 1,
      per_page: 25,
      total_pages: 1,
    },
  },
  sort: {
    sort: null,
    dir: 'asc',
  },
  exports: [],
  context: {},
  visibilityToggle: {
    enabled: true,
    skipColumns: [],
  },
  polling: {
    enabled: false,
    interval: 10000, // 10 seconds in milliseconds
  },
  stickyFilters: {
    enabled: false,
    ttl: 300000, // 5 minutes in milliseconds
  },
  onDataChange: noop,
}

export function DynamicTableProvider({ children, ...props }) {
  // The 'mergedProps' are used to initialize states in the first render.
  // It is essential to understand the precedence order for merging props from different sources:
  //
  // - "DEFAULT_PROPS" : are the base defaults, always applied first to ensure all keys are present in props with defaults
  // - "props" : passed as component props, override DEFAULT_PROPS
  // - "URL params" : (if any) override props from above (only if present)
  // - "Storage params" : (if any) override props from above (only if present and no URL params)
  const mergedProps = useMemo(() => {
    // Step 1: Merge props with defaults
    const mergedSearchQuery = {
      ...DEFAULT_PROPS.search.query,
      ...(props?.search?.query || {}),
    }
    const mergedSearchMeta = {
      ...DEFAULT_PROPS.search.meta,
      ...(props?.search?.meta || {}),
    }
    const mergedSearchFields = ((props?.search?.fields && props.search.fields) || DEFAULT_PROPS.search.fields).map(
      (field) => ({
        value: mergedSearchQuery[field.name],
        ...field,
      }),
    )

    const computedProps = {
      ...DEFAULT_PROPS,
      ...props,
      search: {
        ...DEFAULT_PROPS.search,
        ...(props?.search || {}),
        query: mergedSearchQuery,
        meta: mergedSearchMeta,
        fields: mergedSearchFields,
      },
      sort: {
        ...DEFAULT_PROPS.sort,
        ...(props?.sort || {}),
      },
      visibilityToggle: {
        ...DEFAULT_PROPS.visibilityToggle,
        ...(props?.visibilityToggle || {}),
      },
      polling: {
        ...DEFAULT_PROPS.polling,
        ...(props?.polling || {}),
      },
      stickyFilters: {
        ...DEFAULT_PROPS.stickyFilters,
        ...(props?.stickyFilters || {}),
      },
    }

    // Step 2: Set in the fields the value from query (if any)
    computedProps.search.fields = computedProps.search.fields.map((field) => {
      const fieldValue = computedProps.search.query[field.name] || field.value
      return { ...field, value: fieldValue }
    })

    // Step 3: Handle overrides from URL or Storage (if any)
    const [locationPath] = (props.url || '').split('?', 2)
    const queryFromURL = getQueryFromURL()
    const sortFromURL = getSortFromURL()
    const pageFromURL = getPageFromURL()
    const queryFromStorage = getQueryFromStorage(locationPath)
    const pageFromStorage = getPageFromStorage(locationPath)
    const sortFromStorage = getSortFromStorage(locationPath)

    const handleOverride = (query, sort, page) => {
      computedProps.search.fields = computedProps.search.fields.map((field) => {
        return { ...field, value: query[field.name] }
      })
      computedProps.sort = { sort: sort.sort, dir: sort.dir }
      computedProps.search.meta.current_page = page.page || 1
    }

    const any = (object) => Object.keys(object).length > 0

    if (any(queryFromURL) || any(sortFromURL) || any(pageFromURL)) {
      handleOverride(queryFromURL, sortFromURL, pageFromURL)
    } else if (
      (any(queryFromStorage) || any(sortFromStorage) || any(pageFromStorage)) &&
      computedProps.stickyFilters.enabled
    ) {
      handleOverride(queryFromStorage, sortFromStorage, pageFromStorage)
    }

    // Step 4: Lastly, update the search.query with values from fields (considering they were just updated)
    computedProps.search.query = computedProps.search.fields.reduce((data, field) => {
      const fieldValue = field?.default || field.value
      // biome-ignore lint/performance/noAccumulatingSpread: todo: fix this
      return fieldValue ? { ...data, [field.name]: fieldValue } : data
    }, computedProps.search.query)

    return computedProps
    // biome-ignore lint/correctness/useExhaustiveDependencies: todo: not a good idea to add props as dependency
  }, [props])

  // ----------------------------------------
  // STATES
  // ----------------------------------------
  const [query, setQuery] = useState(mergedProps.search.query)
  const [sort, setSort] = useState(mergedProps.sort)
  const [page, setPage] = useState(mergedProps.search.meta.current_page)
  const [data, setData] = useState(mergedProps.data)
  const [columns, setColumns] = useState(mergedProps.columns)
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState(mergedProps.search.meta)
  const onDataChange = mergedProps.onDataChange

  // ----------------------------------------
  // EFFECTS
  // ----------------------------------------
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    onDataChange(data)
  }, [data])

  const context = useMemo(
    () => ({
      props: mergedProps,
      state: {
        data,
        setData,
        query,
        setQuery,
        sort,
        setSort,
        page,
        setPage,
        columns,
        setColumns,
        loading,
        setLoading,
        meta,
        setMeta,
      },
    }),
    [mergedProps, data, query, sort, page, columns, loading, meta],
  )

  return <DynamicTableContext.Provider value={context}>{children}</DynamicTableContext.Provider>
}
